/**
 * Display timestamps in Asia/Kolkata (IST).
 * API responses use the backend's human-readable IST string, e.g.
 * "01 Jul 2026, 01:25 PM IST" (see app.utils.timezone.to_ist_iso_for_json).
 */

export const IST_TIMEZONE = "Asia/Kolkata";

const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
const _MONTHS: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};
const PRETTY_IST_RE = /^(\d{1,2}) ([A-Za-z]{3}) (\d{4}), (\d{1,2}):(\d{2}) (AM|PM) IST$/;

/** Parse the backend's "01 Jul 2026, 01:25 PM IST" display string into a real Date. */
function parsePrettyIST(raw: string): Date | null {
  const m = PRETTY_IST_RE.exec(raw);
  if (!m) return null;
  const month = _MONTHS[m[2]];
  if (month === undefined) return null;
  const day = Number(m[1]);
  const year = Number(m[3]);
  let hour = Number(m[4]) % 12;
  if (m[6] === "PM") hour += 12;
  const minute = Number(m[5]);
  const utcMs = Date.UTC(year, month, day, hour, minute, 0) - IST_OFFSET_MS;
  const d = new Date(utcMs);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Parse any API datetime — the backend's pretty IST string
 * ("01 Jul 2026, 01:25 PM IST"), a plain ISO string, or a Date.
 * Use this instead of `new Date(apiValue)` anywhere a timestamp from the
 * API needs to be sorted, diffed, or reformatted.
 */
export function parseInstant(input: string | Date): Date | null {
  if (typeof input === "string") {
    const raw = input.trim();
    if (!raw) return null;
    const pretty = parsePrettyIST(raw);
    if (pretty) return pretty;
    const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
    const normalized = raw.includes(" ") ? raw.replace(" ", "T") : raw;
    const d = new Date(hasOffset ? normalized : `${normalized}Z`);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  }
  const d = input;
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/** yyyy-mm-dd in IST — useful for grouping thread days. */
export function istDayKey(input: string | Date): string {
  const d = parseInstant(input);
  if (!d) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** e.g. 4 May 2026 */
export function formatIST(dateString: string | null | undefined): string {
  const d = parseInstant(dateString ?? "");
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: IST_TIMEZONE,
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}

/** e.g. 4 May 2026, 6:30 pm (12-hour, matches web-frontend's format). */
export function formatISTDateTime(dateString: string | null | undefined): string {
  const d = parseInstant(dateString ?? "");
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: IST_TIMEZONE,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

/** e.g. 4 May 2026 (numeric month, short) */
export function formatISTMonthDayYear(dateString: string | null | undefined): string {
  const d = parseInstant(dateString ?? "");
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: IST_TIMEZONE,
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

function formatISTMonthDayShortInner(d: Date): string {
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: IST_TIMEZONE,
    day: "numeric",
    month: "short",
  }).format(d);
}

/** e.g. 4 May — for compact rows */
export function formatISTMonthDayShort(dateString: string | null | undefined): string {
  const d = parseInstant(dateString ?? "");
  if (!d) return "—";
  return formatISTMonthDayShortInner(d);
}

/** Time only in IST (12-hour). */
export function formatISTTimeShort(input: string | Date | null | undefined): string {
  if (input == null || input === "") return "";
  const d = parseInstant(input);
  if (!d) return "";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: IST_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

/** Relative phrasing (past). Older than ~5 weeks → short IST calendar date. */
export function timeAgo(dateString: string | null | undefined): string {
  if (!dateString) return "—";
  const d = parseInstant(dateString);
  if (!d) return "—";
  const then = d.getTime();
  const sec = Math.floor((Date.now() - then) / 1000);
  if (sec < 45) return "just now";
  const rtf = new Intl.RelativeTimeFormat("en-IN", { numeric: "auto" });
  const minutes = Math.floor(sec / 60);
  if (minutes < 60) return rtf.format(-minutes, "minute");
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return rtf.format(-hours, "hour");
  const days = Math.floor(hours / 24);
  if (days < 7) return rtf.format(-days, "day");
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return rtf.format(-weeks, "week");
  return formatISTMonthDayShortInner(d);
}

/** Chat thread list: time today in IST, else short month/day in IST. */
export function formatISTChatListTime(iso: string | undefined): string {
  if (!iso) return "";
  const d = parseInstant(iso);
  if (!d) return "";
  if (istDayKey(d) === istDayKey(new Date())) return formatISTTimeShort(d);
  return formatISTMonthDayShortInner(d);
}

/** Session sidebar: Today / Yesterday / short date in IST. */
export function formatISTSessionDayLabel(iso: string): string {
  const d = parseInstant(iso);
  if (!d) return "—";
  const now = new Date();
  if (istDayKey(iso) === istDayKey(now)) return "Today";
  const y = new Date(now.getTime() - 86400000);
  if (istDayKey(iso) === istDayKey(y)) return "Yesterday";
  return formatISTMonthDayShortInner(d);
}

/** Sticky header from yyyy-mm-dd bucket key (UTC-agnostic calendar key). */
export function formatISTWeekdayHeaderFromDayKey(dayKey: string): string {
  if (dayKey === "Unknown" || !dayKey) return "Unknown date";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return dayKey;
  const d = new Date(`${dayKey}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: IST_TIMEZONE,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(d);
}
